import React, { useState } from 'react';
import { Search, Filter, AlertTriangle, ArrowUpRight, ArrowDownRight, RefreshCw, Box, Package, TrendingUp, ChevronLeft, ChevronRight, Eye, Edit, Download } from 'lucide-react';

const Inventory = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSeller, setFilterSeller] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Static Mock Data
  const inventoryData = [
    { id: 1, sku: 'PLA-IND-001', name: 'Monstera Deliciosa', category: 'Indoor', stock: 124, status: 'In Stock', threshold: 20, lastUpdated: 'Today, 10:30 AM', seller: 'Green Thumb', price: 450 },
    { id: 2, sku: 'PLA-AIR-042', name: 'Snake Plant', category: 'Air Purifying', stock: 8, status: 'Low Stock', threshold: 15, lastUpdated: 'Yesterday, 3:15 PM', seller: 'City Gardens', price: 299 },
    { id: 3, sku: 'PLA-OUT-015', name: 'Rose Bush (Red)', category: 'Outdoor', stock: 45, status: 'In Stock', threshold: 10, lastUpdated: '2 days ago', seller: 'Flora Bliss', price: 350 },
    { id: 4, sku: 'PLA-MED-009', name: 'Aloe Vera', category: 'Medicinal', stock: 0, status: 'Out of Stock', threshold: 50, lastUpdated: 'Today, 08:15 AM', seller: 'Herbals Co.', price: 150 },
    { id: 5, sku: 'POT-CER-101', name: 'Ceramic Pot (White)', category: 'Accessories', stock: 210, status: 'In Stock', threshold: 50, lastUpdated: '1 week ago', seller: 'PaudhaWala', price: 299 },
    { id: 6, sku: 'PLA-IND-089', name: 'Fiddle Leaf Fig', category: 'Indoor', stock: 12, status: 'Low Stock', threshold: 20, lastUpdated: 'Yesterday, 11:00 AM', seller: 'Green Thumb', price: 850 },
    { id: 7, sku: 'PLA-FLO-023', name: 'Peace Lily', category: 'Flowering', stock: 34, status: 'In Stock', threshold: 15, lastUpdated: '3 days ago', seller: 'Flora Bliss', price: 399 },
  ];

  const stats = {
    totalItems: inventoryData.reduce((sum, item) => sum + item.stock, 0),
    lowStock: inventoryData.filter(i => i.status === 'Low Stock').length,
    outOfStock: inventoryData.filter(i => i.status === 'Out of Stock').length,
    totalValue: inventoryData.reduce((sum, item) => sum + (item.price * item.stock), 0)
  };

  const sellers = ['All Sellers', 'Green Thumb', 'City Gardens', 'Flora Bliss', 'Herbals Co.', 'PaudhaWala'];

  const filteredItems = inventoryData.filter(item => {
    const matchesSearch = item.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          item.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSeller = filterSeller === 'all' || filterSeller === 'All Sellers' || item.seller === filterSeller;
    return matchesSearch && matchesSeller;
  });

  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const paginatedItems = filteredItems.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const getStatusConfig = (status) => {
    switch (status) {
      case 'In Stock': return { label: 'In Stock', color: '#10b981', bg: '#d1fae5' };
      case 'Low Stock': return { label: 'Low Stock', color: '#f59e0b', bg: '#fed7aa' };
      case 'Out of Stock': return { label: 'Out of Stock', color: '#ef4444', bg: '#fee2e2' };
      default: return { label: status, color: '#64748b', bg: '#f1f5f9' };
    }
  };

  return (
    <div className="admin-inventory-modern">
      {/* Header */}
      <div className="inventory-header">
        <div>
          <h1 className="page-title">Inventory Control</h1>
          <p className="page-subtitle">Monitor stock levels across all sellers</p>
        </div>
        <div className="header-actions">
          <button className="btn-export">
            <Download size={16} />
            Export
          </button>
          <button className="btn-primary">
            <RefreshCw size={16} />
            Sync Inventory
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon green">
            <Box size={20} />
          </div>
          <div>
            <h3>{stats.totalItems.toLocaleString()}</h3>
            <span>Total Items Managed</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon orange">
            <AlertTriangle size={20} />
          </div>
          <div>
            <h3>{stats.lowStock}</h3>
            <span>Low Stock Alerts</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon red">
            <ArrowDownRight size={20} />
          </div>
          <div>
            <h3>{stats.outOfStock}</h3>
            <span>Out of Stock</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon purple">
            <TrendingUp size={20} />
          </div>
          <div>
            <h3>₹{stats.totalValue.toLocaleString()}</h3>
            <span>Inventory Value</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-box">
          <Search size={18} />
          <input 
            type="text" 
            placeholder="Search SKU or product name..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-selects">
          <select className="filter-select" value={filterSeller} onChange={(e) => setFilterSeller(e.target.value)}>
            {sellers.map(seller => (
              <option key={seller} value={seller === 'All Sellers' ? 'all' : seller}>{seller}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Inventory Table */}
      <div className="table-container">
        <div className="table-responsive">
          <table className="inventory-table">
            <thead>
              <tr>
                <th>SKU</th>
                <th>Product</th>
                <th>Seller</th>
                <th>Current Stock</th>
                <th>Min. Threshold</th>
                <th>Status</th>
                <th>Last Updated</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedItems.map((item) => {
                const statusConfig = getStatusConfig(item.status);
                const isLowStock = item.stock <= item.threshold && item.stock > 0;
                const isOutOfStock = item.stock === 0;
                
                return (
                  <tr key={item.id} className={isLowStock ? 'warning-row' : isOutOfStock ? 'danger-row' : ''}>
                    <td className="sku-cell">{item.sku}</td>
                    <td className="product-cell">
                      <div className="product-name">{item.name}</div>
                    </td>
                    <td className="seller-cell">{item.seller}</td>
                    <td className={`stock-cell ${isLowStock ? 'low' : isOutOfStock ? 'out' : ''}`}>
                      <span className="stock-value">{item.stock}</span>
                      <span className="stock-unit">pcs</span>
                    </td>
                    <td className="threshold-cell">{item.threshold} pcs</td>
                    <td className="status-cell">
                      <span className="status-badge" style={{ backgroundColor: statusConfig.bg, color: statusConfig.color }}>
                        {statusConfig.label}
                      </span>
                    </td>
                    <td className="date-cell">{item.lastUpdated}</td>
                    <td className="text-right">
                      <div className="action-buttons">
                        <button className="action-btn view" title="View Details">
                          <Eye size={16} />
                        </button>
                        <button className="action-btn edit" title="Edit Stock">
                          <Edit size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="pagination-wrapper">
          <div className="pagination-info">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredItems.length)} of {filteredItems.length} items
          </div>
          <div className="pagination-buttons">
            <button 
              className={`page-btn ${currentPage === 1 ? 'disabled' : ''}`} 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft size={16} /> Previous
            </button>
            {[...Array(Math.min(totalPages, 5))].map((_, i) => {
              let pageNum = i + 1;
              if (totalPages > 5 && currentPage > 3) {
                pageNum = currentPage - 3 + i;
                if (pageNum > totalPages) return null;
              }
              return (
                <button 
                  key={i}
                  className={`page-number ${currentPage === pageNum ? 'active' : ''}`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}
            {totalPages > 5 && currentPage < totalPages - 2 && <span className="page-dots">...</span>}
            {totalPages > 5 && currentPage < totalPages - 2 && (
              <button className="page-number" onClick={() => setCurrentPage(totalPages)}>
                {totalPages}
              </button>
            )}
            <button 
              className={`page-btn ${currentPage === totalPages ? 'disabled' : ''}`} 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      
    </div>
  );
};

export default Inventory;